import { useCallback, useEffect } from "react";
import { isMobile } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import { IChildForm } from "../../../";
import SearchAutocomplete from "../../../../../components/form/searchAutocomplete";
import useDealership from "../../../../../hooks/useDealership";
import { Dealership } from "../../../../../models/dealership";
import { fetchDealerships } from "../../../../../api/fetchDealerships";

const DealershipChildForm = (props: IChildForm) => {
  const { getDealershipByDN, dealership } = useDealership();

  const fetchDealershipsCallback = useCallback(
    (searchTerm: string) => fetchDealerships(searchTerm, searchTerm),
    []
  );

  useEffect(() => {
    if (props.occurrence?.dealershipDn) {
      getDealershipByDN(props.occurrence.dealershipDn);
    }
  }, [props.occurrence?.dealershipDn]);

  const isTower = props.persona === 'TOWER';
  const hasDnAlreadySet = Boolean(props.occurrence?.dealershipDn);

  const isDisabled = hasDnAlreadySet && !isTower;

  return (
    <div className="w-full my-6 p-6 pb-0 border rounded">
      <div id="dealershipDn-container" className="flex flex-col md:flex-row md:justify-between w-full gap-4 md:gap-6">
        {!props.hiddenFields.includes("dealershipId") &&
          !props.occurrence?.scheduleNumber && (
            <div
              className={`form-group form-group-lg w-full ${
                props.hasError && props.requiredFields.includes("dealershipDn") && !props.occurrence?.dealershipDn
                  ? "has-error"
                  : ""
              }`}
            >
              <label htmlFor="dealershipId" className="truncate">
                Buscar concessionária
              </label>
              <SearchAutocomplete
                id="dealershipId"
                placeholder="Buscar concessionária pelo nome..."
                fetchOptions={fetchDealershipsCallback}
                optionLabel={{
                  name: "Nome",
                  address: "Endereço",
                  neighborhood: "Bairro",
                  city: "Cidade",
                  state: "Estado",
                }}
                onSelectedOption={(dealership: Dealership) => {
                  props.setOccurrenceValue("dealershipDn", dealership.id);
                  props.setOccurrenceValue("dealershipName", dealership.name);
                  props.setOccurrenceValue("dealershipRegional", dealership.regional ?? null);
                  props.setOccurrenceValue("dealershipCell", dealership.cell ?? null);
                  props.setOccurrenceValue("dealershipUniqueId", dealership.cnpj ?? null);
                  props.setOccurrenceValue("dealershipLocation", dealership.address ?? null);
                  props.setOccurrenceValue("dealershipRepresentative", dealership.representative ?? null);
                }}
                disabled={isDisabled}
              />
            </div>
          )}
      </div>

      <div
        className={`flex justify-between w-full gap-6 ${
          isMobile() ? "flex-wrap" : ""
        }`}
      >
        {!props.hiddenFields.includes("dealershipName") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipName") &&
              !props.occurrence?.dealershipName
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="dealershipName" className="truncate">
              Nome da concessionária
              {props.requiredFields.includes("dealershipName") ? " *" : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="dealershipName"
              placeholder="Nome da concessionária"
              disabled={true}
              value={props.occurrence?.dealershipName ?? dealership?.name ?? ""}
              maxLength={100}
            />
          </div>
        )}

        {!props.hiddenFields.includes("dealershipDn") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipDn") &&
              !props.occurrence?.dealershipDn
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="dn" className="truncate">
              DN{props.requiredFields.includes("dealershipDn") ? " *" : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="dn"
              placeholder="DN"
              disabled={true}
              value={props.occurrence?.dealershipDn ?? dealership?.id ?? ""}
              maxLength={100}
            />
          </div>
        )}
      </div>

      <div
        className={`flex justify-between w-full gap-6 ${
          isMobile() ? "flex-wrap" : ""
        }`}
      >
        {!props.hiddenFields.includes("dealershipUniqueId") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipUniqueId") &&
              !props.occurrence?.dealershipUniqueId
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="cnpj" className="truncate">
              CNPJ
              {props.requiredFields.includes("dealershipUniqueId") ? " *" : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="cnpj"
              placeholder="CNPJ"
              disabled={true}
              value={props.occurrence?.dealershipUniqueId ?? dealership?.cnpj ?? ""}
              maxLength={14}
            />
          </div>
        )}

        {!props.hiddenFields.includes("dealershipLocation") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipLocation") &&
              !props.occurrence?.dealershipLocation
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="location" className="truncate">
              Local
              {props.requiredFields.includes("dealershipLocation") ? " *" : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="location"
              placeholder="Local"
              disabled={true}
              value={props.occurrence?.dealershipLocation ?? dealership?.address ?? ""}
              maxLength={100}
            />
          </div>
        )}

        {!props.hiddenFields.includes("dealershipRepresentative") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipRepresentative") &&
              !props.occurrence?.dealershipRepresentative
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="representative" className="truncate">
              Representante
              {props.requiredFields.includes("dealershipRepresentative")
                ? " *"
                : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="representative"
              placeholder="Representante"
              disabled={true}
              value={props.occurrence?.dealershipRepresentative ?? dealership?.representative ?? ""}
              maxLength={100}
            />
          </div>
        )}
      </div>

      <div
        className={`flex justify-between w-full gap-6 ${
          isMobile() ? "flex-wrap" : ""
        }`}
      >
        {!props.hiddenFields.includes("dealershipRegional") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipRegional") &&
              !props.occurrence?.dealershipRegional
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="regional" className="truncate">
              Regional
              {props.requiredFields.includes("dealershipRegional") ? " *" : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="regional"
              placeholder="Regional"
              disabled={true}
              value={props.occurrence?.dealershipRegional ?? dealership?.regional ?? ""}
              maxLength={100}
            />
          </div>
        )}

        {!props.hiddenFields.includes("dealershipPhone") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipPhone") &&
              !props.occurrence?.dealershipPhone
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="cellNumber" className="truncate">
              Número da célula
              {props.requiredFields.includes("dealershipPhone") ? " *" : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="cellNumber"
              placeholder="Número da célula"
              disabled={true}
              value={props.occurrence?.dealershipPhone ?? dealership?.cell ?? ""}
              maxLength={15}
            />
          </div>
        )}

        {/* {!props.hiddenFields.includes("dealershipDemarcatedArea") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipDemarcatedArea") &&
              !props.occurrence?.dealershipDemarcatedArea
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="demarcatedArea" className="truncate">
              Área demarcada
              {props.requiredFields.includes("dealershipDemarcatedArea")
                ? " *"
                : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="demarcatedArea"
              placeholder="Área demarcada"
              disabled={
                props.disabledAllFields ||
                props.disabledFields.includes("dealershipDemarcatedArea")
              }
              value={props.occurrence?.dealershipDemarcatedArea}
              onChange={(e) =>
                props.occurrence &&
                props.setOccurrenceValue(
                  "dealershipDemarcatedArea",
                  e.target.value
                )
              }
              maxLength={100}
            />
          </div>
        )} */}
      </div>
    </div>
  );
};

export default DealershipChildForm;
